// Phase 20 · Community V3 · Create event form
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Video, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
    { value: "webinar", label: "Webinar", desc: "Presentazione con slide + Q&A finale" },
    { value: "workshop", label: "Workshop", desc: "Sessione hands-on con esercizi + template" },
    { value: "live_stream", label: "Live stream", desc: "Streaming live public open-ended" },
    { value: "qa_ama", label: "Q&A / AMA", desc: "Ask Me Anything · Q&A driven" },
];

const CATEGORIES = ["marketing", "seo", "sales", "growth", "ai-tools", "automation", "analytics", "design", "product", "ops", "hr", "finance", "legal", "customer-success"];

export default function CreateEventPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [form, setForm] = useState({
        event_type: "webinar",
        title: "",
        description: "",
        category_slug: "marketing",
        scheduled_start: "",
        duration_minutes: 60,
        streaming_embed_url: "",
        cover_image_url: "",
        rules: "",
        visibility: "public",
        org_id: "",
    });
    const [busy, setBusy] = useState(false);
    const [flags, setFlags] = useState({});

    useEffect(() => {
        fetch(`${API}/api/community/flags`, { credentials: "include" })
            .then((r) => r.ok ? r.json() : {})
            .then(setFlags).catch(() => {});
    }, []);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.title.trim() || form.title.trim().length < 3) return toast.error("Title 3–200 chars");
        if (!form.scheduled_start) return toast.error("Scheduled start required");
        if (form.visibility === "corporate" && !form.org_id.trim()) return toast.error("org_id required for corporate visibility");
        setBusy(true);
        try {
            const payload = {
                ...form,
                scheduled_start: new Date(form.scheduled_start).toISOString(),
                duration_minutes: Number(form.duration_minutes) || 60,
            };
            if (form.visibility !== "corporate") delete payload.org_id;
            const r = await fetch(`${API}/api/community/events`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(payload),
            });
            const data = await r.json();
            if (!r.ok) {
                const err = data?.detail?.error || data?.detail?.[0]?.msg || "Create failed";
                toast.error(err);
                return;
            }
            toast.success("Event created!");
            navigate(`/events/${data.slug || data.id}`);
        } catch { toast.error("Network error"); }
        setBusy(false);
    };

    const showPrivate = !!flags?.COMMUNITY_PRIVATE_ENABLED;
    const showCorporate = !!flags?.COMMUNITY_CORPORATE_ENABLED;
    const anyVisibilityOn = showPrivate || showCorporate;

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter" data-testid="create-event-page">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/events" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Events
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold">Create event</h1>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Event type</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {TYPES.map((t) => (
                            <button
                                key={t.value}
                                type="button"
                                onClick={() => setField("event_type", t.value)}
                                className={`p-3 rounded-xl border transition-all text-left ${form.event_type === t.value ? "border-[#FFFFFF] bg-[#FFFFFF]/10" : "border-[#1A1A1A] hover:border-[#FFFFFF]/30"}`}
                                data-testid={`create-type-${t.value}`}
                            >
                                <p className="text-sm font-semibold text-neutral-100">{t.label}</p>
                                <p className="text-[10px] text-neutral-500 mt-1">{t.desc}</p>
                            </button>
                        ))}
                    </div>
                </section>

                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Title</label>
                    <input
                        type="text"
                        value={form.title}
                        onChange={(e) => setField("title", e.target.value)}
                        maxLength={200}
                        placeholder="How to scale to 100k MRR with cold email"
                        className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="create-title-input"
                    />
                </section>

                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Description</label>
                    <textarea
                        value={form.description}
                        onChange={(e) => setField("description", e.target.value)}
                        maxLength={5000}
                        rows={4}
                        placeholder="What will attendees learn? Agenda, prerequisites, etc."
                        className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="create-desc-input"
                    />
                </section>

                <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Category</label>
                        <select
                            value={form.category_slug}
                            onChange={(e) => setField("category_slug", e.target.value)}
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="create-category"
                        >
                            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1 inline-flex items-center gap-1">
                            <Calendar size={11} /> Start (UTC)
                        </label>
                        <input
                            type="datetime-local"
                            value={form.scheduled_start}
                            onChange={(e) => setField("scheduled_start", e.target.value)}
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="create-start-input"
                        />
                    </div>
                </section>

                <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Duration (min)</label>
                        <input
                            type="number"
                            min={5}
                            max={720}
                            value={form.duration_minutes}
                            onChange={(e) => setField("duration_minutes", e.target.value)}
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="create-duration-input"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1 inline-flex items-center gap-1">
                            <Video size={11} /> Streaming embed URL <span className="text-[10px] normal-case text-neutral-500">(YouTube / Vimeo / Twitch / Loom)</span>
                        </label>
                        <input
                            type="url"
                            value={form.streaming_embed_url}
                            onChange={(e) => setField("streaming_embed_url", e.target.value)}
                            placeholder="https://youtube.com/live/…"
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="create-streaming-input"
                        />
                    </div>
                </section>

                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Cover image URL (optional)</label>
                    <input
                        type="url"
                        value={form.cover_image_url}
                        onChange={(e) => setField("cover_image_url", e.target.value)}
                        placeholder="https://…"
                        className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="create-cover-input"
                    />
                </section>

                <section>
                    <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Ground rules (optional)</label>
                    <textarea
                        value={form.rules}
                        onChange={(e) => setField("rules", e.target.value)}
                        maxLength={2000}
                        rows={2}
                        placeholder="Be respectful, no self-promo, questions welcomed."
                        className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                        data-testid="create-rules-input"
                    />
                </section>

                {anyVisibilityOn && (
                    <section>
                        <label className="block text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1 inline-flex items-center gap-1">
                            <Lock size={11} /> Visibility
                        </label>
                        <select
                            value={form.visibility}
                            onChange={(e) => setField("visibility", e.target.value)}
                            className="w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-3 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                            data-testid="create-visibility-input"
                        >
                            <option value="public">Public — discoverable + open RSVP</option>
                            {showPrivate && <option value="private">Private — invite / approval only</option>}
                            {showCorporate && <option value="corporate">Corporate — organization members only</option>}
                        </select>
                        {form.visibility === "corporate" && (
                            <input
                                type="text"
                                value={form.org_id}
                                onChange={(e) => setField("org_id", e.target.value)}
                                placeholder="Organization ID (community_orgs.id)"
                                className="mt-2 w-full bg-[#111111] border border-[#1A1A1A] rounded-xl px-4 py-2 text-sm text-neutral-100 focus:outline-none focus:border-[#FFFFFF]"
                                data-testid="create-org-id-input"
                            />
                        )}
                    </section>
                )}

                <div className="flex items-center justify-between pt-2">
                    <p className="text-[10px] text-neutral-500 flex items-center gap-1">
                        <Sparkles size={11} /> Public V3 · 3 events/day/user · signed in as @{user?.username || user?.email}
                    </p>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        className="px-5 py-2 bg-white text-neutral-900 font-semibold rounded-xl text-sm hover:opacity-90 disabled:opacity-40"
                        data-testid="create-submit-btn"
                    >
                        {busy ? "Creating…" : "Create event"}
                    </button>
                </div>
            </main>
        </div>
    );
}
